import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ContextHelp } from "../../ui";
import { Input } from "../../ui/UIElements/Input";
import { normalizeHexColor } from "../../../lib/aiAgentWebviewColors";

/**
 * One brand color: a native picker over a swatch plus a hex text input.
 * The text keeps what the user types until it is a valid `#rrggbb`; blur
 * discards an incomplete draft and shows the current value again.
 */
const ColorPicker = ({ id, label, pickerLabel, value, onChange, disabled }) => {
  const [draft, setDraft] = useState(null);

  const handleText = (event) => {
    const text = event.target.value;
    setDraft(text);
    const color = normalizeHexColor(text, null);
    if (color) onChange(color);
  };

  return (
    <div>
      <label htmlFor={id} className="text-foreground mb-1 block text-xs">
        {label}
      </label>
      <div className="flex gap-2">
        <div
          className="border-border relative h-10 w-12 shrink-0 overflow-hidden rounded-md border"
          style={{ backgroundColor: value }}
        >
          <input
            type="color"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            aria-label={pickerLabel}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          />
        </div>
        <Input
          id={id}
          type="text"
          value={draft ?? value}
          onChange={handleText}
          onBlur={() => setDraft(null)}
          disabled={disabled}
          maxLength={7}
          spellCheck={false}
          containerClassName="flex-1"
          className="font-mono"
        />
      </div>
    </div>
  );
};

/**
 * Primary and secondary colors of the WhatsApp guided webview
 * (`settings.webview_primary_color` / `settings.webview_secondary_color`).
 * Used by both the create and the edit agent modals, WhatsApp agents only.
 */
const AgentWebviewColorsField = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation("aiAgents");

  const pickerFor = (field) => ({
    id: `agent-webview-${field}-color`,
    label: t(`webviewColors.${field}`),
    pickerLabel: t("webviewColors.pickerAria", {
      color: t(`webviewColors.${field}`),
    }),
    value: value[field],
    onChange: (color) => onChange({ ...value, [field]: color }),
    disabled,
  });

  return (
    <div>
      <div className="mb-2 flex items-center gap-1">
        <span className="text-foreground text-sm font-medium">
          {t("webviewColors.label")}
        </span>
        <ContextHelp
          ariaLabel={t("webviewColors.help.ariaLabel")}
          whatIs={t("webviewColors.help.whatIs")}
          note={t("webviewColors.help.note")}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ColorPicker {...pickerFor("primary")} />
        <ColorPicker {...pickerFor("secondary")} />
      </div>
    </div>
  );
};

export default AgentWebviewColorsField;
